// guestStorage — localStorage-backed mirror of all server-side user data.
//
// All Habano features must work for guests (per iter97 audit). We persist
// saves, VIP reminders, spend logs, custom locations etc. in localStorage so
// the app is fully usable without a session. When a guest later verifies via
// WhatsApp (POST /auth/wa/verify), `migrateToAccount()` is called to push the
// local state to the new account in a single transaction.
//
// Storage keys (all under the `habano.` namespace):
//   habano.saved              — array of { target_id, target_type, saved_at }
//   habano.vip_reminders      — array of { business_id, saved_at }
//   habano.spend_log          — array of { id, target_type, target_id, amount,
//                                          currency, date, note, created_at }
//   habano.custom_locations   — array of { id, name, lat, lng, address, ... }
//   habano.last_phone         — last phone the guest used (already used)
//   habano.viewPref.override  — tourist|local override (already used)
//
// All helpers are sync and safe to call in render — they `try/catch` around
// JSON.parse so a corrupted entry never crashes the app.

const KEY = {
  saved: "habano.saved",
  vip: "habano.vip_reminders",
  spend: "habano.spend_log",
  customLocations: "habano.custom_locations",
  checkins: "habano.checkins",
};

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota or disabled */ }
}

function uid() {
  return "g_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

// ── SAVED (favorites) ──────────────────────────────────────────────────────
export function getSaved() {
  const list = read(KEY.saved, []);
  if (!Array.isArray(list)) return [];
  // Tolerate legacy string-only entries that were stored as bare IDs.
  return list.map((x) => (typeof x === "string" ? { target_id: x, target_type: "business" } : x)).filter((x) => x?.target_id);
}

export function getSavedIds(target_type) {
  return getSaved().filter((s) => !target_type || s.target_type === target_type).map((s) => s.target_id);
}

export function isSaved(target_id, target_type = "business") {
  return getSaved().some((s) => s.target_id === target_id && s.target_type === target_type);
}

export function toggleSaved(target_id, target_type = "business") {
  const list = getSaved();
  const idx = list.findIndex((s) => s.target_id === target_id && s.target_type === target_type);
  let nextSaved;
  if (idx >= 0) {
    list.splice(idx, 1);
    nextSaved = false;
  } else {
    list.push({ target_id, target_type, saved_at: new Date().toISOString() });
    nextSaved = true;
  }
  write(KEY.saved, list);
  return nextSaved;
}

// ── VIP REMINDERS ──────────────────────────────────────────────────────────
export function getVipReminders() {
  const list = read(KEY.vip, []);
  return Array.isArray(list) ? list : [];
}

export function hasVipReminder(business_id) {
  return getVipReminders().some((r) => r.business_id === business_id);
}

export function toggleVipReminder(business_id) {
  const list = getVipReminders();
  const idx = list.findIndex((r) => r.business_id === business_id);
  if (idx >= 0) { list.splice(idx, 1); write(KEY.vip, list); return false; }
  list.push({ business_id, saved_at: new Date().toISOString() });
  write(KEY.vip, list);
  return true;
}

// ── SPEND LOG ──────────────────────────────────────────────────────────────
export function getSpendLog() {
  const list = read(KEY.spend, []);
  return Array.isArray(list) ? list : [];
}

export function getSpendForTarget(target_type, target_id) {
  return getSpendLog().filter((e) => e.target_type === target_type && e.target_id === target_id);
}

export function appendSpend(entry) {
  const list = getSpendLog();
  const doc = {
    id: uid(),
    created_at: new Date().toISOString(),
    ...entry,
  };
  list.push(doc);
  write(KEY.spend, list);
  return doc;
}

export function updateSpend(id, patch) {
  const list = getSpendLog();
  const idx = list.findIndex((e) => e.id === id);
  if (idx < 0) return null;
  list[idx] = { ...list[idx], ...patch, updated_at: new Date().toISOString() };
  write(KEY.spend, list);
  return list[idx];
}

export function deleteSpend(id) {
  const list = getSpendLog().filter((e) => e.id !== id);
  write(KEY.spend, list);
}

// ── CUSTOM LOCATIONS ───────────────────────────────────────────────────────
export function getCustomLocations() {
  const list = read(KEY.customLocations, []);
  return Array.isArray(list) ? list : [];
}

export function addCustomLocation(doc) {
  const list = getCustomLocations();
  const entry = { id: uid(), created_at: new Date().toISOString(), ...doc };
  list.push(entry);
  write(KEY.customLocations, list);
  return entry;
}

// iter184 — Idempotent upsert used by the logged-in code path in
// SaveThisSpotSheet. When the API returns a canonical row we mirror it
// into localStorage so the map + sheet can render it without waiting
// for the next API round-trip. If the id already exists we replace it,
// otherwise we append.
export function upsertCustomLocation(row) {
  if (!row || !row.id) return null;
  const list = getCustomLocations();
  const idx = list.findIndex((c) => c.id === row.id);
  if (idx >= 0) list[idx] = { ...list[idx], ...row };
  else list.push(row);
  write(KEY.customLocations, list);
  return row;
}

export function updateCustomLocation(id, patch) {
  const list = getCustomLocations();
  const idx = list.findIndex((c) => c.id === id);
  if (idx < 0) return null;
  list[idx] = { ...list[idx], ...patch, updated_at: new Date().toISOString() };
  write(KEY.customLocations, list);
  return list[idx];
}

export function deleteCustomLocation(id) {
  write(KEY.customLocations, getCustomLocations().filter((c) => c.id !== id));
}

// ── CHECK-INS ──────────────────────────────────────────────────────────────
// Stored as { id, target_type, target_id, target_name, created_at }.
// Duplicate guard: within DUPLICATE_WINDOW_MS we skip and return
// { duplicate: true } so callers can show "Already checked in recently".
const DUPLICATE_WINDOW_MS = 60 * 60 * 1000; // 1 hour per spec

export function getCheckins() {
  const list = read(KEY.checkins, []);
  return Array.isArray(list) ? list : [];
}

export function getCheckinsForTarget(target_type, target_id) {
  return getCheckins().filter((c) => c.target_type === target_type && c.target_id === target_id);
}

export function getRecentCheckin(target_type, target_id) {
  const cutoff = Date.now() - DUPLICATE_WINDOW_MS;
  return getCheckinsForTarget(target_type, target_id)
    .find((c) => new Date(c.created_at).getTime() >= cutoff) || null;
}

export function appendCheckin({ target_type = "business", target_id, target_name = null, lat = null, lng = null } = {}) {
  if (!target_id) return { ok: false, error: "missing_target" };
  const dup = getRecentCheckin(target_type, target_id);
  if (dup) return { ok: false, duplicate: true, existing: dup };
  const list = getCheckins();
  const entry = {
    id: uid(),
    target_type,
    target_id,
    target_name,
    lat,
    lng,
    created_at: new Date().toISOString(),
  };
  list.push(entry);
  write(KEY.checkins, list);
  return { ok: true, entry };
}


// ── MIGRATION (called after WhatsApp verify succeeds) ─────────────────────
export function buildMigrationPayload() {
  const payload = {
    saved: getSaved(),
    vip_reminders: getVipReminders(),
    spend_log: getSpendLog(),
    custom_locations: getCustomLocations(),
    checkins: getCheckins(),
  };
  const total =
    payload.saved.length + payload.vip_reminders.length +
    payload.spend_log.length + payload.custom_locations.length +
    payload.checkins.length;
  if (!total) return null;
  return payload;
}

export function clearGuestData() {
  for (const k of Object.values(KEY)) {
    try { localStorage.removeItem(k); } catch { /* ignore */ }
  }
}

export async function migrateToAccount(api) {
  const payload = buildMigrationPayload();
  if (!payload) return { migrated: 0 };
  try {
    const { data } = await api.post("/me/migrate-guest", payload);
    clearGuestData();
    return data;
  } catch (e) {
    // Don't blow up the login flow on a migration failure — log + keep data.
    console.warn("[Habano] guest migrate failed:", e?.response?.data || e?.message);
    return { migrated: 0, error: e?.response?.data?.detail || e?.message };
  }
}
